import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { CheckCircle, Heart, Calendar, Home } from "lucide-react";

export default function DonationThankYou() {
  const [searchParams] = useSearchParams();
  const [donation, setDonation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("checking");

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollDonationStatus();
    } else {
      setLoading(false);
      setStatus("success");
    }
  }, [sessionId]);

  const pollDonationStatus = async (attempts = 0) => {
    const maxAttempts = 15;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("pending");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/donations/status/${sessionId}`);
      
      if (response.data.status === "completed") {
        setDonation(response.data.donation);
        setStatus("success");
        setLoading(false);
        return;
      }
      // Treat Stripe's "paid" payment_status as completed too (covers race conditions)
      if (response.data.payment_status === "paid") {
        setDonation(response.data.donation || null);
        setStatus("success");
        setLoading(false);
        return;
      }

      setTimeout(() => pollDonationStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking donation status:", error);
      // Retry on transient errors instead of immediately failing
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollDonationStatus(attempts + 1), pollInterval);
      } else {
        setStatus("pending");
        setLoading(false);
      }
    }
  };

  return (
    <Layout>
      <section className="section-spacing" data-testid="thank-you-page">
        <div className="container-custom max-w-2xl mx-auto text-center">
          {loading ? (
            <div>
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-deep-blue mx-auto mb-6"></div>
              <h1 className="text-heading-2 text-deep-blue mb-4">Processing Your Donation...</h1>
              <p className="text-slate-600">Please wait while we confirm your payment.</p>
            </div>
          ) : status === "success" ? (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-heading-1 text-deep-blue mb-4">Thank You!</h1>
              <p className="text-xl text-slate-600 mb-8">
                Your generous donation helps us spread prostate cancer awareness and save lives.
              </p>
              
              {donation && (
                <div className="bg-slate-50 p-6 rounded-lg mb-8 text-left">
                  <h3 className="font-semibold text-deep-blue mb-4">Donation Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Amount:</span>
                      <span className="font-semibold">${donation.amount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Type:</span>
                      <span>{donation.recurring ? "Monthly Recurring" : "One-Time"}</span>
                    </div>
                    {donation.dedication && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">In Honor/Memory Of:</span>
                        <span>{donation.dedication}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-4">
                    A receipt has been sent to your email address.
                  </p>
                </div>
              )}

              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/">
                  <Button className="btn-primary" data-testid="home-btn">
                    <Home className="w-4 h-4 mr-2" />
                    Back to Home
                  </Button>
                </Link>
                <Link to="/events">
                  <Button variant="outline" data-testid="events-btn">
                    <Calendar className="w-4 h-4 mr-2" />
                    View Events
                  </Button>
                </Link>
              </div>
            </>
          ) : status === "pending" ? (
            <>
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-10 h-10 text-yellow-600" />
              </div>
              <h1 className="text-heading-2 text-deep-blue mb-4">Payment Processing</h1>
              <p className="text-slate-600 mb-8">
                Your donation is being processed. You'll receive a confirmation email shortly.
              </p>
              <Link to="/">
                <Button className="btn-primary">Back to Home</Button>
              </Link>
            </>
          ) : (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-heading-2 text-deep-blue mb-4">Something Went Wrong</h1>
              <p className="text-slate-600 mb-8">
                We couldn't confirm your donation. Please contact us if you believe this is an error.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/donate">
                  <Button className="btn-primary">Try Again</Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline">Contact Us</Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
